
import React, { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface ConsentState {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  aiData: boolean;
}

interface ConsentRecord {
  timestamp: string;
  consent: ConsentState;
  userAgent: string;
  ipAddress?: string;
}

const CONSENT_STORAGE_KEY = 'cookie_consent';
const CONSENT_EXPIRY_MONTHS = 12;

export const useConsentManager = () => {
  // Safe initialization with error handling and null checks
  const [consent, setConsent] = useState<ConsentState>(() => {
    try {
      // Ensure localStorage is available
      if (typeof window === 'undefined' || !window.localStorage) {
        return {
          essential: true,
          analytics: false,
          marketing: false,
          aiData: false,
        };
      }

      return {
        essential: true, // Always true
        analytics: false,
        marketing: false,
        aiData: false,
      };
    } catch (error) {
      console.error('Error initializing consent state:', error);
      return {
        essential: true,
        analytics: false,
        marketing: false,
        aiData: false,
      };
    }
  });

  const [isLoading, setIsLoading] = useState(() => {
    try {
      return true;
    } catch (error) {
      console.error('Error initializing loading state:', error);
      return false;
    }
  });

  // Load saved consent on mount
  useEffect(() => {
    loadSavedConsent();
  }, []);

  const loadSavedConsent = async () => {
    try {
      const saved = localStorage.getItem(CONSENT_STORAGE_KEY);
      if (saved) {
        const parsed: ConsentRecord = JSON.parse(saved);
        
        // Check if consent has expired
        const consentDate = new Date(parsed.timestamp);
        const expiryDate = new Date();
        expiryDate.setMonth(expiryDate.getMonth() - CONSENT_EXPIRY_MONTHS);
        
        if (consentDate > expiryDate) {
          setConsent(parsed.consent);
        } else {
          // Expired consent, clear it
          localStorage.removeItem(CONSENT_STORAGE_KEY);
        }
      }
    } catch (error) {
      console.error('Error loading consent:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const saveConsent = async (newConsent: ConsentState) => {
    const consentRecord: ConsentRecord = {
      timestamp: new Date().toISOString(),
      consent: newConsent,
      userAgent: navigator.userAgent,
    };

    // Save to localStorage
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consentRecord));
    setConsent(newConsent);

    // Save to database for compliance audit trail
    try {
      await supabase.functions.invoke('log-consent', {
        body: {
          consent: newConsent,
        }
      });
    } catch (error) {
      console.error('Error logging consent to database:', error);
    }

    // Apply consent to tracking systems
    applyConsent(newConsent);
  };

  const applyConsent = (consentState: ConsentState) => {
    // Use safe analytics wrapper to respect development mode
    if (consentState.analytics) {
      import('@/utils/safeAnalytics').then(({ safeAnalytics }) => {
        safeAnalytics.updateConsent({
          analytics_storage: 'granted'
        });
      }).catch(() => {
        // Silent fail if analytics not available
      });
    } else {
      import('@/utils/safeAnalytics').then(({ safeAnalytics }) => {
        safeAnalytics.updateConsent({
          analytics_storage: 'denied'
        });
      }).catch(() => {
        // Silent fail if analytics not available
      });
    }

    // Marketing consent through safe wrapper
    if (consentState.marketing) {
      import('@/utils/safeAnalytics').then(({ safeAnalytics }) => {
        safeAnalytics.updateConsent({
          ad_storage: 'granted',
          ad_user_data: 'granted',
          ad_personalization: 'granted'
        });
      }).catch(() => {
        // Silent fail if analytics not available
      });
    } else {
      import('@/utils/safeAnalytics').then(({ safeAnalytics }) => {
        safeAnalytics.updateConsent({
          ad_storage: 'denied',
          ad_user_data: 'denied',
          ad_personalization: 'denied'
        });
      }).catch(() => {
        // Silent fail if analytics not available
      });
    }

    // AI Data consent is handled by components that need it
    // They should check the consent state before processing data
  };

  const updateConsent = useCallback((type: keyof ConsentState, value: boolean) => {
    try {
      if (type === 'essential') return; // Can't change essential
      const newConsent = { ...consent, [type]: value };
      setConsent(newConsent);
    } catch (error) {
      console.error('Error updating consent:', error);
    }
  }, [consent]);

  const acceptAll = useCallback(() => {
    const allConsent: ConsentState = {
      essential: true,
      analytics: true,
      marketing: true,
      aiData: true,
    };
    saveConsent(allConsent);
  }, []);

  const rejectNonEssential = useCallback(() => {
    const essentialOnly: ConsentState = {
      essential: true,
      analytics: false,
      marketing: false,
      aiData: false,
    };
    saveConsent(essentialOnly);
  }, []);

  const hasUserConsented = useCallback(() => {
    const saved = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!saved) return false;
    
    try {
      const parsed: ConsentRecord = JSON.parse(saved);
      const consentDate = new Date(parsed.timestamp);
      const expiryDate = new Date();
      expiryDate.setMonth(expiryDate.getMonth() - CONSENT_EXPIRY_MONTHS);
      
      return consentDate > expiryDate;
    } catch {
      return false;
    }
  }, []);

  const clearConsent = useCallback(() => {
    localStorage.removeItem(CONSENT_STORAGE_KEY);
    setConsent({
      essential: true,
      analytics: false,
      marketing: false,
      aiData: false,
    });
  }, []);

  return {
    consent,
    updateConsent,
    acceptAll,
    rejectNonEssential,
    hasUserConsented,
    clearConsent,
    isLoading,
    saveConsent,
  };
};
